import React, { useEffect, useRef, useState } from 'react';
import './LogsDateRangePicker.css';

const pad2 = (n: number) => String(n).padStart(2, '0');

function toIsoDateLocal(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseIsoLocal(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  const y = +m[1];
  const mo = +m[2] - 1;
  const day = +m[3];
  const d = new Date(y, mo, day);
  if (d.getFullYear() !== y || d.getMonth() !== mo || d.getDate() !== day) return null;
  return d;
}

function formatRu(ymd: string): string {
  const d = parseIsoLocal(ymd);
  if (!d) return '';
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function normalizeTime(value: string, fallback: string): string {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
  if (!m) return fallback;
  const h = Math.min(23, Math.max(0, Number(m[1])));
  const min = Math.min(59, Math.max(0, Number(m[2])));
  return `${pad2(h)}:${pad2(min)}`;
}

function openTimePicker(input: HTMLInputElement | null) {
  if (!input || typeof input.showPicker !== 'function') return;
  try {
    input.showPicker();
  } catch {
    /* пикер уже открыт или браузер блокирует повторный вызов */
  }
}

/** Понедельник = 0 … воскресенье = 6 */
function weekdayMon0(d: Date): number {
  const w = d.getDay();
  return w === 0 ? 6 : w - 1;
}

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

function daysGrid(year: number, monthIndex: number): (number | null)[] {
  const first = new Date(year, monthIndex, 1);
  const lead = weekdayMon0(first);
  const dim = new Date(year, monthIndex + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  return cells;
}

function inRange(iso: string, from: string, to: string): boolean {
  return iso >= from && iso <= to;
}

export interface LogsDateRangePickerProps {
  dateFrom: string;
  dateTo: string;
  onChange: (from: string, to: string) => void;
  /** Выбор времени для ts_from / ts_to (страница номеров). */
  withTime?: boolean;
  timeFrom?: string;
  timeTo?: string;
  onTimeChange?: (from: string, to: string) => void;
}

const POPOVER_WIDTH = 320;
const POPOVER_HEIGHT = 360;
const POPOVER_HEIGHT_WITH_TIME = 460;

const LogsDateRangePicker: React.FC<LogsDateRangePickerProps> = ({
  dateFrom,
  dateTo,
  onChange,
  withTime = false,
  timeFrom = '00:00',
  timeTo = '23:59',
  onTimeChange,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => {
    const base = (dateFrom && parseIsoLocal(dateFrom)) || (dateTo && parseIsoLocal(dateTo)) || new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  /** Первая точка в текущей сессии выбора (второй клик фиксирует диапазон) */
  const [firstPick, setFirstPick] = useState<string | null>(null);
  const [hoverPick, setHoverPick] = useState<string | null>(null);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const [draftFrom, setDraftFrom] = useState(dateFrom);
  const [draftTo, setDraftTo] = useState(dateTo);
  const [draftTimeFrom, setDraftTimeFrom] = useState(timeFrom);
  const [draftTimeTo, setDraftTimeTo] = useState(timeTo);

  const rangeFrom = withTime ? draftFrom : dateFrom;
  const rangeTo = withTime ? draftTo : dateTo;
  const popoverHeight = withTime ? POPOVER_HEIGHT_WITH_TIME : POPOVER_HEIGHT;

  const closePicker = () => {
    setOpen(false);
    setFirstPick(null);
    setHoverPick(null);
  };

  const applyDraft = () => {
    let fromYmd: string;
    let toYmd: string;
    if (firstPick) {
      fromYmd = firstPick;
      toYmd = firstPick;
    } else {
      fromYmd = draftFrom || dateFrom;
      toYmd = draftTo || dateTo || fromYmd;
    }
    if (!fromYmd || !toYmd) return;
    let tf = normalizeTime(draftTimeFrom, '00:00');
    let tt = normalizeTime(draftTimeTo, '23:59');
    if (fromYmd === toYmd && tf > tt) {
      [tf, tt] = [tt, tf];
    }
    onChange(fromYmd, toYmd);
    onTimeChange?.(tf, tt);
    closePicker();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePicker();
    };
    document.addEventListener('keydown', onKey);
    if (withTime) {
      return () => document.removeEventListener('keydown', onKey);
    }
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        closePicker();
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, withTime]);

  useEffect(() => {
    if (!open) return;
    const base = (dateFrom && parseIsoLocal(dateFrom)) || (dateTo && parseIsoLocal(dateTo)) || new Date();
    setView(new Date(base.getFullYear(), base.getMonth(), 1));
    setDraftFrom(dateFrom);
    setDraftTo(dateTo);
    setDraftTimeFrom(normalizeTime(timeFrom, '00:00'));
    setDraftTimeTo(normalizeTime(timeTo, '23:59'));
  }, [open, dateFrom, dateTo, timeFrom, timeTo]);

  useEffect(() => {
    if (!open || !rootRef.current) return;

    const place = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(POPOVER_WIDTH, window.innerWidth - 24);
      const spaceBelow = window.innerHeight - rect.bottom - 12;
      const openUp = spaceBelow < popoverHeight && rect.top > spaceBelow;
      const left = Math.min(Math.max(12, rect.right - width), window.innerWidth - width - 12);
      setPopoverStyle({
        position: 'fixed',
        left,
        width,
        zIndex: 80,
        ...(openUp
          ? { top: 'auto', bottom: window.innerHeight - rect.top + 8 }
          : { top: rect.bottom + 8, bottom: 'auto' }),
      });
    };

    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, popoverHeight]);

  const y = view.getFullYear();
  const m = view.getMonth();
  const grid = daysGrid(y, m);

  const previewFrom = firstPick && hoverPick ? (firstPick <= hoverPick ? firstPick : hoverPick) : null;
  const previewTo = firstPick && hoverPick ? (firstPick <= hoverPick ? hoverPick : firstPick) : null;

  const formatPart = (ymd: string, hm?: string) => {
    if (!ymd) return '';
    const date = formatRu(ymd);
    return withTime && hm ? `${date} ${normalizeTime(hm, '00:00')}` : date;
  };

  const displayLeft = previewFrom
    ? formatRu(previewFrom)
    : firstPick
      ? formatRu(firstPick)
      : rangeFrom
        ? formatRu(rangeFrom)
        : '—';
  const displayRight = previewTo
    ? formatRu(previewTo)
    : firstPick
      ? '…'
      : rangeTo
        ? formatRu(rangeTo)
        : '—';
  const triggerLeft = dateFrom ? formatPart(dateFrom, timeFrom) : '';
  const triggerRight = dateTo ? formatPart(dateTo, timeTo) : '';
  const showAll = !dateFrom && !dateTo && !firstPick;

  const onDayClick = (day: number) => {
    const iso = toIsoDateLocal(new Date(y, m, day));

    if (firstPick === null) {
      setFirstPick(iso);
      setHoverPick(null);
      return;
    }

    let a = firstPick;
    let b = iso;
    if (a > b) [a, b] = [b, a];
    setFirstPick(null);
    setHoverPick(null);
    if (withTime) {
      setDraftFrom(a);
      setDraftTo(b);
      return;
    }
    onChange(a, b);
    setOpen(false);
  };

  const onDayEnter = (day: number) => {
    if (firstPick === null) return;
    setHoverPick(toIsoDateLocal(new Date(y, m, day)));
  };

  const isInSelectedRange = (iso: string): boolean => {
    if (firstPick && !hoverPick) return iso === firstPick;
    if (previewFrom && previewTo) return inRange(iso, previewFrom, previewTo);
    if (rangeFrom && rangeTo) return inRange(iso, rangeFrom, rangeTo);
    if (rangeFrom && rangeTo === '' && iso === rangeFrom) return true;
    return false;
  };

  const isRangeEnd = (iso: string): boolean => {
    if (previewFrom && previewTo) return iso === previewFrom || iso === previewTo;
    if (firstPick && !hoverPick) return iso === firstPick;
    if (rangeFrom && rangeTo) return iso === rangeFrom || iso === rangeTo;
    return false;
  };

  const shiftMonth = (delta: number) => {
    setView(new Date(y, m + delta, 1));
  };

  const clearRange = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('', '');
    onTimeChange?.('00:00', '23:59');
    setDraftFrom('');
    setDraftTo('');
    setDraftTimeFrom('00:00');
    setDraftTimeTo('23:59');
    setFirstPick(null);
    setHoverPick(null);
  };

  return (
    <div className="logs-daterange" ref={rootRef}>
      <button
        type="button"
        className="logs-daterange__trigger"
        onClick={() => {
          setOpen((v) => !v);
          setFirstPick(null);
          setHoverPick(null);
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span className="logs-daterange__value">
          {showAll ? (
            <span className="logs-daterange__all">За весь период</span>
          ) : (
            <>
              <span className="logs-daterange__part">{triggerLeft || displayLeft}</span>
              <span className="logs-daterange__sep" aria-hidden>
                —
              </span>
              <span className="logs-daterange__part">{triggerRight || displayRight}</span>
            </>
          )}
        </span>
        <span className="logs-daterange__icon" aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M7 2v3M17 2v3M4 9h16M6 6h12a2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2z"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
          </svg>
        </span>
      </button>

      {!showAll && (
        <button type="button" className="logs-daterange__clear" onClick={clearRange} title="Сбросить период">
          ×
        </button>
      )}

      {open && (
        <div
          className={'logs-daterange__popover' + (withTime ? ' logs-daterange__popover--with-time' : '')}
          role="dialog"
          aria-label={withTime ? 'Выбор периода и времени' : 'Выбор периода дат'}
          style={popoverStyle}
        >
          <div className="logs-daterange__hint">
            {firstPick !== null
              ? 'Выберите вторую дату'
              : withTime
                ? rangeFrom && rangeTo
                  ? 'Укажите время и нажмите «Применить»'
                  : 'Выберите дату или диапазон, затем время'
                : 'Выберите дату или диапазон'}
          </div>
          <div className="logs-daterange__nav">
            <button type="button" className="logs-daterange__nav-btn" onClick={() => shiftMonth(-1)} aria-label="Предыдущий месяц">
              ‹
            </button>
            <span className="logs-daterange__month">
              {MONTHS[m]} {y}
            </span>
            <button type="button" className="logs-daterange__nav-btn" onClick={() => shiftMonth(1)} aria-label="Следующий месяц">
              ›
            </button>
          </div>
          <div className="logs-daterange__weekdays">
            {WEEKDAYS.map((w) => (
              <div key={w} className="logs-daterange__wd">
                {w}
              </div>
            ))}
          </div>
          <div className="logs-daterange__grid" onMouseLeave={() => setHoverPick(null)}>
            {grid.map((day, idx) =>
              day === null ? (
                <div key={`e-${idx}`} className="logs-daterange__cell logs-daterange__cell--empty" />
              ) : (
                (() => {
                  const iso = toIsoDateLocal(new Date(y, m, day));
                  const inSel = isInSelectedRange(iso);
                  const end = isRangeEnd(iso);
                  return (
                    <button
                      key={iso}
                      type="button"
                      className={
                        'logs-daterange__cell' +
                        (inSel ? ' logs-daterange__cell--in-range' : '') +
                        (end ? ' logs-daterange__cell--edge' : '')
                      }
                      onClick={() => onDayClick(day)}
                      onMouseEnter={() => onDayEnter(day)}
                    >
                      {day}
                    </button>
                  );
                })()
              )
            )}
          </div>
          {withTime && (
            <>
              <div className="logs-daterange__preview" aria-live="polite">
                <span>{displayLeft}</span>
                <span className="logs-daterange__sep">—</span>
                <span>{displayRight}</span>
              </div>
              <div className="logs-daterange__time">
                <label
                  className="logs-daterange__time-field"
                  onClick={(e) => {
                    const input = e.currentTarget.querySelector('input[type="time"]');
                    openTimePicker(input instanceof HTMLInputElement ? input : null);
                  }}
                >
                  <span>С</span>
                  <input
                    type="time"
                    value={draftTimeFrom}
                    step={60}
                    onChange={(e) => setDraftTimeFrom(e.target.value || '00:00')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applyDraft();
                      }
                    }}
                    aria-label="Время начала"
                  />
                </label>
                <label
                  className="logs-daterange__time-field"
                  onClick={(e) => {
                    const input = e.currentTarget.querySelector('input[type="time"]');
                    openTimePicker(input instanceof HTMLInputElement ? input : null);
                  }}
                >
                  <span>По</span>
                  <input
                    type="time"
                    value={draftTimeTo}
                    step={60}
                    onChange={(e) => setDraftTimeTo(e.target.value || '23:59')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applyDraft();
                      }
                    }}
                    aria-label="Время окончания"
                  />
                </label>
              </div>
              <button
                type="button"
                className="logs-daterange__apply"
                onClick={applyDraft}
                disabled={!(draftFrom || draftTo || dateFrom || dateTo || firstPick)}
              >
                Применить
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default LogsDateRangePicker;
